import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Share2, Copy, Check, Mail, Facebook, MessageSquare, MessageCircle, Link2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Clean canonical destination — never the current browser URL (anchors, preview
// hosts and UTM test parameters must never end up in a share).
const SHARE_URL = "https://pendli.no/";
const SHARE_TITLE = "Pendli";
const SHARE_TEXT =
  "Ta en titt på Pendli – en ny måte å leie ut ledig parkeringsplass eller finne parkering nær stasjonen.";
const SHARE_MESSAGE = `${SHARE_TEXT} ${SHARE_URL}`;
const EMAIL_SUBJECT = "Ta en titt på Pendli";

const VARIANTS = {
  huseier: {
    title: "🎉 Takk for interessen!",
    body: [
      "Du har registrert interesse som vert.",
      "Du er blant de første som får vite når Pendli lanseres i Asker.",
      "Da kan du leie ut parkeringsplassen din til pendlere når du ikke bruker den selv.",
      "Har du venner, familie eller naboer med en ledig parkeringsplass eller innkjørsel? Hjelp oss gjerne med å bygge Pendli-fellesskapet ved å dele tjenesten.",
    ],
  },
  pendler: {
    title: "🚆 Du er med!",
    body: [
      "Du har registrert interesse som pendler.",
      "Du er blant de første som får vite når Pendli lanseres i Asker.",
      "Da kan du leie en privat parkeringsplass nær stasjonen når du skal ta toget – uten å lete etter ledig plass.",
      "Kjenner du andre som pendler til jobb eller skole? Hjelp oss gjerne med å bygge Pendli-fellesskapet ved å dele tjenesten.",
    ],
  },
  both: {
    title: "🚗 Fantastisk!",
    body: [
      "Du har registrert interesse både som vert og pendler.",
      "Du er blant de første som får vite når Pendli lanseres i Asker.",
      "Det betyr at du både kan leie ut en parkeringsplass og finne parkering når du trenger det.",
      "Hvis du kjenner noen som kunne hatt nytte av Pendli, hadde vi satt stor pris på om du delte tjenesten.",
    ],
  },
};

const THANKS = {
  title: "❤️ Tusen takk!",
  body: [
    "Du hjelper oss med å bygge Pendli-fellesskapet i Asker.",
    "Hver eneste deling gjør det lettere å få både flere verter og flere pendlere med før lansering.",
    "Vi gleder oss til å ha deg med på reisen!",
  ],
};

// Animated SVG checkmark inside a circle
function AnimatedCheck() {
  return (
    <div className="relative w-20 h-20 md:w-24 md:h-24 mx-auto mb-6">
      <svg viewBox="0 0 100 100" className="w-full h-full" data-testid="success-check">
        <motion.circle
          cx="50" cy="50" r="46"
          fill="none"
          stroke="#E8A800"
          strokeWidth="4"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
        />
        <motion.path
          d="M30 52 L45 67 L72 38"
          fill="none"
          stroke="#E8A800"
          strokeWidth="6"
          strokeLinecap="square"
          strokeLinejoin="miter"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.35, delay: 0.45, ease: "easeOut" }}
        />
      </svg>
    </div>
  );
}

// Tasteful confetti — 14 amber/orange/cream dots scattered
function Confetti() {
  const dots = Array.from({ length: 18 }).map((_, i) => {
    const colors = ["#E8A800", "#FF6B00", "#F5F0E8"];
    return {
      id: `confetti-${i}`,
      color: colors[i % 3],
      left: 5 + Math.random() * 90,
      delay: 0.1 + Math.random() * 0.5,
      x: (Math.random() - 0.5) * 200,
      y: 220 + Math.random() * 80,
      r: 360 + Math.random() * 360,
      size: 6 + Math.random() * 6,
    };
  });
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {dots.map((d) => (
        <motion.span
          key={d.id}
          initial={{ opacity: 0, y: -10, x: 0, rotate: 0 }}
          animate={{ opacity: [0, 1, 1, 0], y: d.y, x: d.x, rotate: d.r }}
          transition={{ duration: 1.6, delay: d.delay, ease: "easeOut" }}
          style={{
            position: "absolute",
            top: "20%",
            left: `${d.left}%`,
            width: d.size,
            height: d.size,
            background: d.color,
          }}
        />
      ))}
    </div>
  );
}

function ShareButton({ icon: Icon, label, onClick, testId, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={ariaLabel || label}
      className="group flex flex-col items-center gap-1.5 p-2 transition-colors"
    >
      <span className="w-12 h-12 md:w-14 md:h-14 bg-amber-deep border border-amber/30 flex items-center justify-center group-hover:border-amber group-hover:bg-amber/15 transition-colors">
        <Icon size={20} className="text-amber group-hover:text-amber-hover" />
      </span>
      <span className="text-[10px] md:text-xs font-bold tracking-widest uppercase text-cream/60 group-hover:text-amber transition-colors">
        {label}
      </span>
    </button>
  );
}

export default function SuccessModal({ open, onClose, variant = "pendler", contact }) {
  const v = VARIANTS[variant] || VARIANTS.pendler;
  const [copied, setCopied] = useState(false);
  const [thanked, setThanked] = useState(false);
  const [isTouch, setIsTouch] = useState(false);
  // Visible feedback for every control: { kind: "ok" | "error", text }
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
      setIsTouch(window.matchMedia("(pointer: coarse)").matches);
    }
  }, []);

  // Reset when reopened
  useEffect(() => {
    if (open) {
      setCopied(false);
      setThanked(false);
      setFeedback(null);
    }
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const track = async (method) => {
    try {
      await axios.post(`${API}/share-events`, {
        role: variant,
        method,
        contact: contact || null,
      });
    } catch (e) {
      console.error("Share tracking failed:", e);
    }
  };

  const onShareSuccess = () => setThanked(true);

  // Visible feedback for every control: { kind: "ok" | "error", text }
  const say = (kind, text) => setFeedback({ kind, text });
  const copyToClipboard = async (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
      throw new Error("Clipboard API unavailable");
    } catch (err) {
      console.warn("Clipboard API failed, using fallback:", err?.name || err);
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, text.length);
        const ok = document.execCommand("copy");
        ta.remove();
        return ok;
      } catch (fallbackErr) {
        console.error("Copy fallback failed:", fallbackErr);
        return false;
      }
    }
  };

  const doCopy = async () => {
    const ok = await copyToClipboard(SHARE_URL);
    setCopied(ok);
    if (ok) {
      say("ok", "Lenke kopiert!");
      track("copy");
    } else {
      say("error", `Kunne ikke kopiere automatisk. Kopier lenken manuelt: ${SHARE_URL}`);
    }
  };

  const doNative = async () => {
    if (typeof navigator.share !== "function") {
      // Fallback so the button always produces a usable result.
      const ok = await copyToClipboard(SHARE_URL);
      setCopied(ok);
      say(
        ok ? "ok" : "error",
        ok
          ? "Nettleseren din støtter ikke deling – lenken er kopiert i stedet."
          : `Deling er ikke støttet. Kopier lenken manuelt: ${SHARE_URL}`
      );
      return;
    }
    try {
      await navigator.share({ title: SHARE_TITLE, text: SHARE_TEXT, url: SHARE_URL });
      track("native");
      onShareSuccess();
    } catch (err) {
      if (err?.name === "AbortError") {
        say("ok", "Deling avbrutt.");
      } else {
        console.error("Native share failed:", err);
        say("error", "Deling feilet. Prøv «Kopier lenke» i stedet.");
      }
    }
  };

  const openExternal = (url, method, features = "noopener,noreferrer") => {
    const win = window.open(url, "_blank", features);
    if (!win) {
      say("error", "Nettleseren blokkerte vinduet. Tillat popup, eller bruk «Kopier lenke».");
      return;
    }
    track(method);
    onShareSuccess();
  };

  const doSms = () => {
    track("sms");
    say("ok", "Åpner meldinger...");
    window.location.href = `sms:?body=${encodeURIComponent(SHARE_MESSAGE)}`;
  };

  // Shares Pendli from the USER's own WhatsApp — no Pendli WhatsApp account involved.
  const doWhatsapp = () =>
    openExternal(`https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`, "whatsapp");

  // Shares Pendli from the USER's own Facebook — no Pendli page involved.
  const doFacebook = () =>
    openExternal(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(SHARE_URL)}`,
      "facebook",
      "noopener,noreferrer,width=600,height=520"
    );

  // Share by email: no recipient — the user picks who to tell.
  const doEmail = () => {
    const href = `mailto:?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${encodeURIComponent(
      `${SHARE_TEXT}\n\n${SHARE_URL}`
    )}`;
    track("email");
    say("ok", "Åpner e-postklienten din...");
    const a = document.createElement("a");
    a.href = href;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="success-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          onClick={onClose}
          aria-modal="true"
          role="dialog"
        >
          <div className="absolute inset-0 bg-ink/85 backdrop-blur-md" />

          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: 10 }}
            transition={{ duration: 0.25, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative w-full max-w-lg bg-ink border border-amber/40 p-6 md:p-10 max-h-[90vh] overflow-y-auto l-bracket-tl l-bracket-tr l-bracket-bl l-bracket-br"
            onClick={(e) => e.stopPropagation()}
            data-testid={`success-modal-${thanked ? "thanks" : variant}`}
          >
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              data-testid="success-modal-close"
              aria-label="Lukk"
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-cream/60 hover:text-amber hover:bg-amber/10 transition-colors"
            >
              <X size={18} />
            </button>

            {!thanked && <Confetti />}

            <div className="relative">
              {!thanked ? (
                <>
                  <AnimatedCheck />
                  <h2
                    className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight tracking-tighter text-center mb-5"
                    style={{ letterSpacing: "-0.03em" }}
                    data-testid="success-modal-title"
                  >
                    {v.title}
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-cream/80 leading-relaxed mb-7">
                    {v.body.map((p) => (
                      <p key={p}>{p}</p>
                    ))}
                  </div>

                  {/* Share grid */}
                  <div className="border-t border-amber/15 pt-6">
                    <p className="text-xs font-bold tracking-widest uppercase text-amber/80 mb-4 text-center">
                      Del Pendli
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
                      <ShareButton
                        icon={Share2}
                        label="Del"
                        onClick={doNative}
                        testId="share-native"
                        ariaLabel="Del Pendli"
                      />
                      <ShareButton
                        icon={copied ? Check : Copy}
                        label={copied ? "Kopiert" : "Kopier"}
                        onClick={doCopy}
                        testId="share-copy"
                        ariaLabel="Kopier lenke"
                      />
                      {isTouch && (
                        <ShareButton
                          icon={MessageSquare}
                          label="SMS"
                          onClick={doSms}
                          testId="share-sms"
                        />
                      )}
                      <ShareButton
                        icon={MessageCircle}
                        label="WhatsApp"
                        onClick={doWhatsapp}
                        testId="share-whatsapp"
                      />
                      <ShareButton
                        icon={Mail}
                        label="E-post"
                        onClick={doEmail}
                        testId="share-email"
                      />
                      <ShareButton
                        icon={Facebook}
                        label="Facebook"
                        onClick={doFacebook}
                        testId="share-facebook"
                      />
                    </div>

                    {feedback && (
                      <p
                        data-testid={`share-feedback-${feedback.kind}`}
                        role="status"
                        aria-live="polite"
                        className={`mb-4 text-sm text-center font-bold break-words ${
                          feedback.kind === "ok" ? "text-amber" : "text-orange"
                        }`}
                      >
                        {feedback.text}
                      </p>
                    )}

                    <button
                      type="button"
                      onClick={doCopy}
                      data-testid="share-copy-link-button"
                      className="w-full h-11 mb-3 border border-amber/40 text-cream/80 hover:text-amber hover:border-amber transition-colors text-sm font-bold tracking-tight inline-flex items-center justify-center gap-2"
                    >
                      <Link2 size={15} />
                      {copied ? "Lenken er kopiert" : "Kopier lenke"}
                    </button>

                    <button
                      type="button"
                      onClick={onClose}
                      data-testid="success-modal-close-button"
                      className="w-full h-11 text-cream/50 hover:text-cream transition-colors text-sm font-bold tracking-tight"
                    >
                      Lukk
                    </button>
                  </div>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  data-testid="success-modal-thanks-content"
                >
                  <div className="text-5xl md:text-6xl text-center mb-5" aria-hidden="true">❤️</div>
                  <h2
                    className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight tracking-tighter text-center mb-5"
                    style={{ letterSpacing: "-0.03em" }}
                  >
                    {THANKS.title}
                  </h2>
                  <div className="space-y-4 text-sm md:text-base text-cream/80 leading-relaxed mb-8 text-center">
                    {THANKS.body.map((p) => <p key={p}>{p}</p>)}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    data-testid="success-modal-done-button"
                    className="w-full h-12 bg-amber text-ink font-display font-bold text-base hover:bg-amber-hover transition-colors"
                  >
                    Ferdig
                  </button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
